import clsx from 'clsx'
import { useQueryClient } from '@tanstack/react-query'
import { Icon } from '@iconify/react'
import { show as showModal } from '@ebay/nice-modal-react'
import {
  Button,
  useCreateReport,
  useGetProjectDomain,
  useGlobalState,
  useShowMessage,
} from '@pepper/ui'
import type { Topic } from '@pepper/utils/src/types/seo'

import { useSelectedCompanyId } from 'utils/functions/misc'

import s from './TopicActions.module.css'

interface Props {
  topic: Topic
  dataIdIndex: number
  onEditTopic: (topic: Topic) => void
  onCreateReport: (topic: Topic) => void
  onGeneratingReportClick: () => void
  className?: string
}

function TopicActions({
  topic,
  dataIdIndex,
  className,
  onEditTopic,
  onGeneratingReportClick,
  onCreateReport,
}: Props): JSX.Element {
  const { permissions, subscription, trialExhaust } = useGlobalState()
  const companyId = useSelectedCompanyId()
  const showMessage = useShowMessage()
  const queryClient = useQueryClient()
  const hasTopicsPermission = permissions?.topics

  const { mutate: createReport, isLoading } = useCreateReport()
  const { data: projectDomain } = useGetProjectDomain({
    data: { companyId, projectId: topic.project_id },
  })

  const isGeneratingReport =
    topic?.crr &&
    ['pending', 'processing'].includes(topic?.crr.generation_status_name)
  const isReportGenerated =
    topic?.crr && topic?.crr.generation_status_name === 'completed'
  const canGenerateReport = topic.keywords?.some(k => k.type === 'primary')
  const failedKeywordGeneration = topic?.keyword_generation_status === 'failed'

  const disableSeoGuidelineGeneration =
    !canGenerateReport && !failedKeywordGeneration

  const domain = projectDomain?.domain
  const isDomainRemoved = !!domain?.deleted_at
  const disableEdit =
    !hasTopicsPermission || isLoading || isDomainRemoved || isGeneratingReport

  const handleCreateReport = () => {
    if (!domain) return

    if (failedKeywordGeneration) {
      onCreateReport(topic)
      return
    }

    const isTrialExpired = subscription?.trialStatus === 'expired'
    const isLimitExhausted = trialExhaust?.seoContentResearch

    if (isTrialExpired || isLimitExhausted) {
      showModal('buy-cmp', {
        ...(isLimitExhausted && {
          id: 'seoContentResearch',
        }),
      })
      return
    }
    onGeneratingReportClick()
    createReport(
      {
        companyId,
        body: {
          topic_id: topic.id,
          topic: topic.name,
          region: domain.region_code,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['project-topics'])
        },
        onError: error => {
          if (error instanceof Error) {
            showMessage({ type: 'error', message: error.message })
          }
        },
      }
    )
  }

  return (
    <section
      className={clsx({
        [s.generating]: isGeneratingReport,
        [String(className)]: !isGeneratingReport,
      })}
    >
      {isGeneratingReport ? (
        <Button
          type="link"
          size="small"
          data-id={`topic-${dataIdIndex}-seo-guidelines-loading`}
          icon={<Icon icon="lucide:loader" />}
          onClick={onGeneratingReportClick}
        >
          Generating SEO Guideline
        </Button>
      ) : (
        !isReportGenerated && (
          <Button
            className={s.btn}
            size="small"
            icon={<Icon icon="lucide:plus" />}
            onClick={handleCreateReport}
            loading={isLoading}
            disabled={disableSeoGuidelineGeneration}
            {...(disableSeoGuidelineGeneration && {
              title: 'Cannot generate SEO Guideline without primary keywords',
            })}
            data-id={`topic-${dataIdIndex}-create-seo-guidelines-button`}
          >
            SEO Guideline
          </Button>
        )
      )}

      <Button
        size="small"
        htmlType="button"
        icon={<Icon icon="lucide:edit-3" width={12} height={12} />}
        iconPlacement="right"
        className={s.edit}
        disabled={disableEdit}
        onClick={() => onEditTopic(topic)}
        {...(isGeneratingReport && {
          title: 'Cannot perform actions while generating SEO Guideline',
        })}
      >
        Edit
      </Button>
    </section>
  )
}

export default TopicActions
